import * as React from "react";
import { useAuth } from "@frontegg/react";
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";

export interface AppError {
  message: string;
}

export interface ApiResponse<T> {
  data: T | null;
  error: AppError | null;
}

export const callExternalApi = async <T,>(options: {
  config: AxiosRequestConfig;
}): Promise<ApiResponse<T>> => {
  try {
    const response: AxiosResponse = await axios(options.config);
    const { data } = response;

    return {
      data,
      error: null,
    };
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError;

      const { response } = axiosError;

      let message = "http request failed";

      if (response && response.statusText) {
        message = response.statusText;
      }

      if (axiosError.message) {
        message = axiosError.message;
      }

      if (response && response.data && (response.data as AppError).message) {
        message = (response.data as AppError).message;
      }

      if (response && response.data && (response.data as string)) {
        message = (response.data as string);
      }

      return {
        data: null,
        error: {
          message,
        },
      };
    }

    return {
      data: null,
      error: {
        message: (error as Error).message,
      },
    };
  }
};

const apiServerUrl = process.env.REACT_APP_API_SERVER_URL;

export const useApi = <T,>(endpoint: string, method: string = "GET") => {
  const { user } = useAuth();

  const makeRequest = React.useCallback(
    async (body: unknown | null = null) => {
      const accessToken = user?.accessToken;

      const config: AxiosRequestConfig = {
        url: `${apiServerUrl}/${endpoint}`,
        method,
        headers: {
          "content-type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      };

      if (body) {
        config.data = body;
      }

      return await callExternalApi<T>({ config });
    },
    [user, endpoint, method]
  );

  return makeRequest;
};

export const useApiEffect = <T,>(
  endpoint: string,
  method: string = "GET",
  body: unknown | null = null
) => {
  const { isAuthenticated } = useAuth();
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<AppError | null>(null);
  const [data, setData] = React.useState<T | null>(null);
  const fetchFromApi = useApi<T>(endpoint, method);

  React.useEffect(() => {
    (async () => {
      setLoading(true);
      if (isAuthenticated) {
        const response = await fetchFromApi(body);
        if (response.error) {
          setError(response.error);
        } else if (response.data) {
          setData(response.data);
        }
        setLoading(false);
      }
    })();
  }, [isAuthenticated, body, fetchFromApi]);

  return { loading, data, error };
};